/* eslint-disable no-console */
import {ReloadOutlined} from '@ant-design/icons';
import {Button, Divider, Flex} from 'antd';
import {isAxiosError} from 'axios';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';

import Loader from '../../components/Loader/Loader';
import PageContent from '../../components/PageContent/PageContent';
import MainTableTitle from '../../components/Table/Utils/MainTableTitle';
import {allRoutes} from '../../constants/Routes';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import emissionCalculation from '../../services/modules/DASHBOARD/emissionCalculation';
import dataLineage from '../../services/modules/DataLineage/dataLineage';
import facilityDetailsAPI from '../../services/modules/OM/facilityDetails';
import yearFilterArray from '../../shared/YearFilterArray';
import useUserStore from '../../store/userStore/userStore';
import {hasUserRole, UserRole} from '../../utils/commonFunctions';
import {sortYearOptions} from '../orgManager/Utils/InfraStructureProfile/constant/sorYear';

import {SubFiltersOptions} from './utils/common';
import DataLineageFilter from './utils/DataLineageFilter';
import DataLineageTable from './utils/DataLineageTable';
import type {DataLineageRow} from './utils/dataLineageTypes';

/** O(1) lookup for `dataBy` → sub-filter metadata (was `.find` per row). */
const SUB_FILTER_BY_DATA_BY = new Map(
  SubFiltersOptions.map((s) => [s.value, s])
);

const DataLineage: React.FC = () => {
  const [, setSearchParams] = useSearchParams();
  const [selectedYear, setSelectedYear] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [mainData, setMainData] = useState<DataLineageRow[]>([]);
  const [selectedFacility, setSelectedFacility] = useState<string>('');
  const [scope, setScope] = useState<string>('');
  const [subFilters, setSubFilters] = useState<string>('');
  const [refreshing, setRefreshing] = useState<boolean>();
  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const {userDetails} = useUserStore.getState();
    facilityDetailsAPI(APITypeEnum.GET_ALL, true)
      .then((facilityDetails) => {
        const facilityYears = sortYearOptions(
          yearFilterArray(
            facilityDetails,
            !(
              hasUserRole(UserRole.Company_Admin) ||
              hasUserRole(UserRole.Company_user)
            )
              ? userDetails?.facility
              : undefined
          )
        );
        const firstYearMain = facilityYears?.[0]?.value;
        const params = new URLSearchParams(window.location.search);
        const searchYear = params.get('selectedYear') ?? '';
        const hasYear = facilityYears?.some(
          (i: {value: string}) => i.value === searchYear
        );
        const setYear = hasYear ? searchYear : firstYearMain ?? '';
        setSelectedYear(setYear);

        const isCompanyUser =
          hasUserRole(UserRole.Company_Admin) ||
          hasUserRole(UserRole.Company_user);

        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('selectedYear', setYear);
          if (!isCompanyUser) {
            next.delete('facility');
          }

          return next;
        });
      })
      .catch((err) => {
        console.error('Error occurred:', err);
      })
      .finally(() => {});
  }, []);

  useEffect(() => {
    if (!selectedYear) {
      return;
    }
    const abortController = new AbortController();
    setIsLoading(true);
    setMainData([]);
    dataLineage(selectedYear, {signal: abortController.signal})
      .then((res) => {
        setMainData((res ?? []) as DataLineageRow[]);
      })
      .catch((err) => {
        if (isAxiosError(err) && err.code === 'ERR_CANCELED') {
          return;
        }
        console.error('Error occurred:', err);
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => {
      abortController.abort();
    };
  }, [selectedYear, refreshKey]);

  const filteredData = useMemo(() => {
    let tempData: DataLineageRow[] = mainData.map((item) => {
      const subFilterObj = item.dataBy
        ? SUB_FILTER_BY_DATA_BY.get(item.dataBy)
        : undefined;

      return {
        ...item,
        subFilter: subFilterObj?.value ?? '',
        scope: subFilterObj?.scope ?? '',
        subFilterLabel: subFilterObj?.label
      };
    });
    if (selectedFacility) {
      tempData = tempData.filter((item) => item.facility === selectedFacility);
    }
    if (scope) {
      tempData = tempData.filter((item) => item.scope === scope);
    }
    if (subFilters) {
      tempData = tempData.filter((item) => item.subFilter === subFilters);
    }

    return tempData.map((item) => ({
      ...item,
      group: subFilters
        ? item.dataLabel
        : selectedFacility
          ? `${item.subFilterLabel} - ${item.dataLabel}`
          : `${item.facilityName} - ${item.subFilterLabel} - ${item.dataLabel}`
    }));
  }, [mainData, selectedFacility, scope, subFilters]);

  const handleYearChange = useCallback(
    (newYear: string) => {
      setSelectedYear(newYear);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('selectedYear', newYear);

        return next;
      });
    },
    [setSearchParams]
  );

  const handleRefreshClick = useCallback(() => {
    setRefreshing(true);
    emissionCalculation(selectedYear)
      .then(() => {
        setRefreshKey((k) => k + 1);
      })
      .catch(() => {})
      .finally(() => {
        setRefreshing(false);
      });
  }, [selectedYear]);

  return (
    <PageContent>
      <Flex justify="space-between" align="center" style={{width: '100%'}}>
        <MainTableTitle title="Data Lineage" showDivider={false} />
        <Flex vertical={false} gap={10} align="center" justify="center">
          <Button
            onClick={() => {
              navigate(`${allRoutes.OM.infraProfile}`);
            }}
            type="primary"
          >
            Upload activity data
          </Button>
          <Button
            type="default"
            icon={<ReloadOutlined />}
            title="Reflecting data from Carbon Accounting"
            loading={refreshing}
            onClick={handleRefreshClick}
          ></Button>
        </Flex>
      </Flex>
      <Divider />
      <DataLineageFilter
        selectedYear={selectedYear}
        setSelectedYear={handleYearChange}
        setSelectedFacility={setSelectedFacility}
        scope={scope}
        setScope={setScope}
        setSubFilters={setSubFilters}
        shouldDisable={refreshing}
      />
      {isLoading ? <Loader /> : <DataLineageTable mainData={filteredData} />}
    </PageContent>
  );
};

export default DataLineage;
